import { useMemo } from 'react';

interface SparklineChartProps {
    /** Data points for the sparkline */
    data: number[];
    /** Optional labels for data points (used in tooltips) */
    labels?: string[];
    /** Chart type */
    chartType: 'line' | 'bar';
    /** Optional color for the sparkline */
    color?: string;
    /** Optional height of the chart in pixels */
    height?: number;
}

// Internal viewBox dimensions for SVG coordinate system
const VIEWBOX_WIDTH = 300;
const VIEWBOX_HEIGHT = 60;

/**
 * Pure SVG sparkline chart - no external dependencies.
 * Renders either a bar chart or line chart based on chartType.
 * Automatically fills the width of its container.
 */
export function SparklineChart({
    data,
    labels,
    chartType,
    color = 'var(--weavable-sparkline-color, #3b82f6)',
    height = 30,
}: SparklineChartProps) {
    const padding = 4;

    const normalizedData = useMemo(() => {
        if (data.length === 0) {
            return [];
        }
        const max = Math.max(...data);
        const min = Math.min(...data, 0);
        const range = max - min || 1;

        return data.map((v) => (v - min) / range);
    }, [data]);

    if (data.length === 0) {
        return null;
    }

    if (chartType === 'bar') {
        const barGap = 2;
        const barWidth = (VIEWBOX_WIDTH - padding * 2) / data.length - barGap;

        return (
            <svg
                width="100%"
                height={height}
                viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
                preserveAspectRatio="none"
                className="weavable-sparkline weavable-sparkline--bar"
                role="img"
                aria-label={`Sparkline bar chart with ${data.length} data points`}
            >
                {normalizedData.map((value, index) => {
                    const barHeight = Math.max(value * (VIEWBOX_HEIGHT - padding * 2), 1);
                    const x = padding + index * (barWidth + barGap);
                    const y = VIEWBOX_HEIGHT - padding - barHeight;

                    return (
                        <rect key={index} x={x} y={y} width={barWidth} height={barHeight} fill={color} opacity={0.85}>
                            {labels?.[index] && <title>{`${labels[index]}: ${data[index]}`}</title>}
                        </rect>
                    );
                })}
            </svg>
        );
    }

    // Line chart
    const points = normalizedData
        .map((value, index) => {
            const x = padding + (index / Math.max(data.length - 1, 1)) * (VIEWBOX_WIDTH - padding * 2);
            const y = VIEWBOX_HEIGHT - padding - value * (VIEWBOX_HEIGHT - padding * 2);
            return `${x},${y}`;
        })
        .join(' ');

    return (
        <svg
            width="100%"
            height={height}
            viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
            preserveAspectRatio="none"
            className="weavable-sparkline weavable-sparkline--line"
            role="img"
            aria-label={`Sparkline line chart with ${data.length} data points`}
        >
            <polyline
                points={points}
                fill="none"
                stroke={color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}
